import React, { useState, useEffect } from 'react';
import { Col, Form, Offcanvas, OffcanvasBody, OffcanvasHeader, Row } from 'reactstrap';
import SimpleBar from 'simplebar-react';
import { getEstablishmentList, getEstablishmentTypeList } from '../../../../helpers/establishment/backend_helper';
import { DatePickerInput, DatesProvider } from '@mantine/dates';
import 'dayjs/locale/es-mx';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import OSelect from '../../../../Components/OElements/OSelect';

const OffcanvasContainer = ({ isOpen, toggle, filters = {}, onSubmit }) => {
    const [selectedDate, setSelectedDate] = useState(filters.dateRange);
    const [selectedEstablishment, setSelectedEstablishment] = useState(null);
    const [selectedEstablishmentType, setSelectedEstablishmentType] = useState(null);

    const [filterState, setFilterState] = useState(filters);
    const [isLoading, setIsLoading] = useState(false);

    const handleDateChange = (date) => {
        setSelectedDate(date);
    };

    const handleEstablishmentSelected = (optionSelected) => {
        setSelectedEstablishment(optionSelected);
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        validation.handleSubmit();

    };

    const validation = useFormik({
        // enableReinitialize: use this flag when initial values needs to be changed
        enableReinitialize: true,
        initialValues: {
            establishment_id: '',
            establishment_type_id: '',
            group_by_artifacts_and_tanks: false,
            date_range: [],
            hide_detail_columns: false, 
        },        
        validationSchema: Yup.object({
            establishment_id: Yup.string().nullable(),
            establishment_type_id: Yup.string().nullable(),
            group_by_artifacts_and_tanks: Yup.boolean(),
            hide_detail_columns: Yup.boolean().nullable(),
            date_range: Yup.array()
        }),
        onSubmit: async (values) => {
            setIsLoading(true);
            const newFilters = { ...filterState };

            if (values.establishment_id) {
                newFilters.establishment_id = values.establishment_id;
                newFilters.establishment_name = selectedEstablishment.name.toUpperCase();
            }

            if (values.establishment_type_id) {
                newFilters.establishment_type_id = values.establishment_type_id;
                newFilters.establishment_type_name = selectedEstablishmentType.name.toUpperCase();
            }

            if (values.date_range && values.date_range.length > 0) {
                newFilters.start_date = values.date_range[0];
                newFilters.end_date = values.date_range[1];
            }

            if (typeof values.group_by_artifacts_and_tanks === 'boolean') {
                newFilters.group_by_artifacts_and_tanks = values.group_by_artifacts_and_tanks;
            }

            if (typeof values.hide_detail_columns === 'boolean') {
                newFilters.hide_detail_columns = values.hide_detail_columns;
            }

            setFilterState(newFilters);
            onSubmit(newFilters);
            setIsLoading(false);
            toggle();
        }

    });

    useEffect(() => {
        if (isOpen) {
            validation.setFieldValue('date_range', (
                filters.start_date &&
                    filters.end_date
                    ? [filters.start_date, filters.end_date]
                    : []
            ));
        }
    }, [isOpen])

    const handleClearFilters = () => {
        const emptyFilters = {};
        setFilterState(emptyFilters);
        onSubmit(emptyFilters);
        validation.resetForm();
    };

    return (
        <Offcanvas
            isOpen={isOpen}
            direction="end"
            toggle={toggle}
            className="border-bottom"
        >
            <OffcanvasHeader toggle={toggle}>
                Filtros
            </OffcanvasHeader>
            <OffcanvasBody className="p-3 ">
                <SimpleBar style={{ height: "100vh" }}>
                    <Form onSubmit={validation.handleSubmit}>
                        <Row className='g-3'>
                            <Col lg={12}>
                                <OSelect
                                    key={`establishment_type_id`}
                                    label='Tipo Establecimiento'
                                    id='establishment_type_id'
                                    name='establishment_type_id'
                                    value={filterState?.establishment_type_id || ''}
                                    onSelect={(option) => {
                                        setSelectedEstablishmentType(option);
                                        validation.setFieldValue('establishment_type_id', option?._id || '');
                                        setFilterState(prev => ({
                                            ...prev,
                                            establishment_type_id: option?._id || ''
                                        }));
                                    }}
                                    requestApi={() => getEstablishmentTypeList({})}
                                    validation={validation}
                                />

                            </Col>
                            <Col lg={12}>
                                <OSelect
                                    key={`establishment_id_${selectedEstablishmentType?._id || null}`}
                                    label='Establecimiento'
                                    id='establishment_id'
                                    name='establishment_id'
                                    value={filterState?.establishment_id || ''}
                                    handleLabelOptions={(item) => `${item.name}`}
                                    onSelect={(option) => {
                                        setSelectedEstablishment(option);
                                        validation.setFieldValue('establishment_id', option?._id || '');
                                        setFilterState(prev => ({
                                            ...prev,
                                            establishment_id: option?._id || ''
                                        }));
                                    }}
                                    requestApi={() => getEstablishmentList({ establishment_type_id: filterState?.establishment_type_id || null })}
                                    validation={validation}
                                />
                            </Col>
                            <Col sm={12}>
                                <DatesProvider settings={{ locale: 'es-mx', firstDayOfWeek: 1, weekendDays: [0], timezone: 'UTC' }}>
                                    <DatePickerInput
                                        key={`date_range`}
                                        type='range'
                                        label='Rango de fecha'
                                        id='date_range'
                                        name='date_range'
                                        placeholder="Seleccione un rango"
                                        value={(validation.values.date_range)}
                                        onChange={(date) => {
                                            const dates = date.filter(item => item != null);
                                            validation.setFieldValue("date_range", (
                                                dates.length
                                                    ? dates
                                                    : []
                                            ));
                                        }}
                                        onBlur={validation.handleBlur}
                                        valueFormat='DD-MM-YYYY'
                                        // maxDate={new Date()}
                                        error={
                                            validation.touched['date_range'] &&
                                                validation.errors['date_range']
                                                ? validation.errors['date_range'] : ''
                                        }
                                        clearable={true}

                                    />
                                </DatesProvider>
                            </Col>
                            <Col sm={12}>
                                <div className="form-check form-switch form-switch-md d-flex flex-column p-0" dir="ltr">
                                    <label className="form-check-label" htmlFor="group_by_artifacts_and_tanks">
                                        ¿Aperturado por Artefacto Naval y Estanque?
                                    </label>
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        style={{ marginLeft: '10px' }}
                                        id="group_by_artifacts_and_tanks"
                                        name="group_by_artifacts_and_tanks"
                                        checked={!!validation.values.group_by_artifacts_and_tanks}
                                        onChange={() => validation.setFieldValue('group_by_artifacts_and_tanks', !validation.values.group_by_artifacts_and_tanks)}
                                    />
                                </div>
                            </Col>
                            <Col sm={12}>
                                <div className="form-check form-switch form-switch-md d-flex flex-column p-0" dir="ltr">
                                    <label className="form-check-label" htmlFor="hide_detail_columns">
                                        ¿Ocultar columnas de los tipos de movimientos?
                                    </label>
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        style={{ marginLeft: '10px' }}
                                        id="hide_detail_columns"
                                        name="hide_detail_columns"
                                        checked={!!validation.values.hide_detail_columns}
                                        onChange={(e) => { validation.setFieldValue('hide_detail_columns', !validation.values.hide_detail_columns); }}
                                    />
                                </div>
                            </Col>
                        </Row>

                        <Row>
                            <Col>
                                <div className="hstack gap-2 mt-4">
                                    <button type='button' className='btn btn-label btn-danger' onClick={handleClearFilters}><i className="ri-filter-off-fill label-icon"></i> Limpiar</button>

                                    <button type='submit' className='btn btn-label btn-primary' id='add-btn' disabled={isLoading}>
                                        {isLoading ? (<i className='label-icon'><Spinner size='sm' /></i>) : (<i className='ri-filter-fill label-icon'></i>)} Filtrar</button>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </SimpleBar>
            </OffcanvasBody>
        </Offcanvas >
    );
};

export default OffcanvasContainer;